// Central engine: single owner of all mutable trading state (ladders, manual/bracket orders, fill ledger, PnL).

import { log as ringLog } from './log';
import { journal } from './journal';

/** A single public print. `t` is ms; trade timestamps carry microseconds. */
export interface Trade {
  t: number;
  priceMc: MC;
  mq: MQ;
  taker: 'yes' | 'no' | null;
}

/** One OHLC bar, in MC; `n` is the trade count, which is what closes a tick bar. */
export interface Bar {
  t0: number;
  t1: number;
  o: MC;
  h: MC;
  l: MC;
  c: MC;
  vol: MQ;
  n: number;
  forming?: boolean;
}

/** One point of quote history. Live samples carry no hi/lo — they are instants. */
export interface HistPoint {
  t: number;
  bid: MC | null;
  ask: MC | null;
  bidHi?: MC | null;
  bidLo?: MC | null;
  askHi?: MC | null;
  askLo?: MC | null;
}
import { eventRows, isBuilding as isEventIndexBuilding, rebuildEventIndex, searchEvents,
  seriesOf, type EventRow, type SeriesRow } from './events';
import type { ExploreEvent, ExploreMarket, ExploreState, VenueEngine, VenueHints } from '../venue/engine';
import { AccountManager, MOBILE_ID, avgPxMc, blankPos, costOf, isMobile, pnlTree, type Account, type PosState } from './accounts';
import { appendFill, appendFills, allFills, getFillOverrides, getMeta, setFillOverrides,
  setMeta, updateFillAccount, type FillRow } from './db';
import {
  MC_SETTLE, MQ_PER_CONTRACT, cashOf, countToMq, dollarsToCash, dollarsToMc, floorContracts,
  type Cash, type MC, type MQ,
} from './money';
import {
  ACTIVE_STATUSES, WS_ORDER_ACTIVE, newMarketState,
  type AccountRow, type Bracket, type FillEvent, type LadderRow, type MarketState, type Order,
  type OrdersOutRow, type PnlSnapshot, type PositionRow, type SettledRow,
  type UiOrder, type UiSnapshot,
} from './types';
import { KalshiAPIError, KalshiClient, type NormalizedOrder, type ShardAllocation, type ShardBalance } from '../kalshi/client';
import { KalshiWs, type WsStatus } from '../kalshi/ws';
import { callBridge } from '../kalshi/bridge';

// client_order_id format: <prefix>-<account-without-dash>-<rand>, e.g. ui-J00001-ab12cd34ef.
const COID_ACCT_RE = /^(?:ui|mm)-(J\d{5})-/;

export function coidFor(accountId: string): string {
  const rand = crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  return `ui-${accountId.replace(/-/g, '')}-${rand}`;
}

export function accountFromCoid(coid: string | null | undefined): string | null {
  const m = COID_ACCT_RE.exec(coid || '');
  return m ? 'J-' + m[1].slice(1) : null;
}

export interface PlaceArgs {
    ticker: string;
    side: 'yes' | 'no';
    /** Price of the side being bought, in MC. */
    priceMc: MC;
    qtyMq: MQ;
    mode: 'post' | 'fok';
    accountId: string;
    source?: 'manual' | 'bracket-hedge';
  /** Journal tag: the arb order and leg this placement is for. */
  tag?: string;
}
export type PlaceResult = { ok: true; order: NormalizedOrder } | { ok: false; error: string; status?: number };

const PT_TZ = 'America/Los_Angeles';

function ptDayStr(d = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PT_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

/** Unix seconds at the most recent 00:00 America/Los_Angeles (DST-safe). */
function ptDayStartEpoch(): number {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PT_TZ, hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const hour = get('hour') % 24;
  const secsIntoDay = hour * 3600 + get('minute') * 60 + get('second');
  return Math.floor(now.getTime() / 1000) - secsIntoDay;
}

export interface EngineEvents {
  snapshot: UiSnapshot;
  pnl: PnlSnapshot;
  fill: FillEvent;
  /** A public print on a subscribed market. Drives live bar formation. */
  trade: { ticker: string; trade: Trade };
  status: { ws: WsStatus; detail?: string; stale: boolean };
  accounts: { leaves: { id: string; name: string }[] };
  log: { level: 'info' | 'warn' | 'error'; msg: string };
}

type Listener<K extends keyof EngineEvents> = (payload: EngineEvents[K]) => void;

export class Engine implements VenueEngine {
  readonly venue = 'kalshi' as const;
  readonly label = 'Kalshi';
  readonly tradingEnabled = true;
  readonly hints: VenueHints = {
    explore: 'Search events… (e.g. pope, fed, oscar)',
    ladder: 'Search contract… (e.g. KXWCGAME)',
    exploreEmpty: 'Event index unavailable — search by ticker in Ladder mode.',
  };
  readonly accounts = new AccountManager();
  readonly states = new Map<string, MarketState>();
  readonly subscribed = new Set<string>();
  private tickerRefs = new Map<string, number>();

  balanceCash: Cash = 0;
  positions: any[] = [];
  settlements: any[] = [];

  /** order_id -> ticker (kills a linear scan); order_id -> account_id. */
  private orderIndex = new Map<string, string>();
  private orderAccounts = new Map<string, string>();
  /** All resting orders across every ticker, including unsubscribed ones. */
  private allResting = new Map<string, { ticker: string; order_id: string; client_order_id: string; side: string }>();

  /** ticker -> account_id -> level_mc -> [bidFills, askFills], today (PT). */
  private fillMarkers = new Map<string, Map<string, Map<MC, [number, number]>>>();
  private markersDay = ptDayStr();

  brackets = new Map<string, Bracket>();
  private bracketHedges = new Map<string, string>(); // hedge order_id -> entry order_id
  private hedgeLocks = new Map<string, Promise<void>>();

  private dirtyTickers = new Set<string>();
  private infoTimers = new Map<string, number>();
  /** Last envelope seq per subscription id, for gap detection. */
  private wsSeq = new Map<number, number>();
  private lastResync = new Map<string, number>();
  private pnlDirty = false;
  private listeners = new Map<keyof EngineEvents, Set<Listener<any>>>();

  private timers: number[] = [];
  private pollTimers = new Map<string, number>();
  private posLock: Promise<void> | null = null;
  private posQueued = false;
  private singleFlight = new Map<string, Promise<void>>();
  private queueFetchPending = new Map<string, Set<string>>();
  private readonly QUEUE_FETCH_DEBOUNCE_MS = 1_500;

  private ws: KalshiWs;
  wsStatus: WsStatus = 'idle';
  /** True until the first fresh snapshot lands after a reconnect; blocks order placement. */
  stale = true;

  private started = false;
  /** fill_id -> account_id, for fills moved by hand. See db.ts. */
  private fillOverrides = new Map<string, string>();

  constructor(readonly client: KalshiClient, wsUrl = '') {
    this.ws = new KalshiWs({
      wsUrl,
      currentTickers: () => [...this.subscribed],
      onMessage: (m) => { void this.handleWsMsg(m); },
      onStatus: (status, detail) => this.onWsStatus(status, detail),
    });
  }

  /** The socket URL comes from the extension, which may not be installed when the page loads. */
  setWsUrl(wsUrl: string): void {
    this.ws.wsUrl = wsUrl;
  }

  /** Has the engine been started? Boot polls this while waiting on the key. */
  get isStarted(): boolean {
    return this.started;
  }

  on<K extends keyof EngineEvents>(event: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(fn);
    return () => set!.delete(fn);
  }

  private emit<K extends keyof EngineEvents>(event: K, payload: EngineEvents[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(payload);
      } catch (e) {
        console.error(`[engine] listener for ${String(event)} threw`, e);
      }
    }
  }

  private log(level: 'info' | 'warn' | 'error', msg: string): void {
    if (level === 'error') console.error('[engine]', msg);
    else if (level === 'warn') console.warn('[engine]', msg);
    else console.info('[engine]', msg);
    // Also into the ring buffer behind the Logs drawer, so it shows the engine's own conclusions, not just REST traffic.
    ringLog(level, 'engine', msg);
    this.emit('log', { level, msg });
  }

  markDirty(ticker: string): void {
    this.dirtyTickers.add(ticker);
  }

  markPnlDirty(): void {
    this.pnlDirty = true;
  }

  /** Coalesced broadcast: fires every 40ms, emits snapshots for dirty tickers. */
  private flush = (): void => {
    if (this.dirtyTickers.size) {
      const tickers = [...this.dirtyTickers];
      this.dirtyTickers.clear();
      for (const t of tickers) this.emit('snapshot', this.buildUiSnapshot(t));
    }
    if (this.pnlDirty) {
      this.pnlDirty = false;
      this.emit('pnl', this.buildPnlSnapshot());
    }
  };

  private onWsStatus(status: WsStatus, detail?: string): void {
    this.wsStatus = status;
    if (status !== 'open') {
      this.stale = true;
    } else {
      // Clear book state so a delayed/dropped snapshot can't leave phantom levels, then force a fresh pull.
      for (const ticker of this.subscribed) {
        const st = this.states.get(ticker);
        if (st) {
          st.yesBids.clear();
          st.noBids.clear();
          st.estAhead.clear();
          st.lastWsTs = 0;
        }
        void this.fetchOpenOrders(ticker);
      }
      void this.fetchAllPositions();
      if (!this.subscribed.size) this.stale = false; // nothing to go stale
    }
    this.emit('status', { ws: status, detail, stale: this.stale });
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    // Load overrides before anything reads the ledger, or a live fill/rebuild would undo a manual move.
    this.fillOverrides = new Map(Object.entries(await getFillOverrides()));
    await this.accounts.load();
    await this.loadBrackets();
    this.emit('accounts', { leaves: this.accounts.listLeaves() });

    await this.client.resyncClock();
    await this.fetchBalance();
    await this.fetchAllPositions();
    await this.fetchAllOrders();
    await this.seedMarkers();

    void this.fetchAllSettlements();
    this.ws.start();

    this.timers.push(window.setInterval(this.flush, 40));
    this.timers.push(window.setInterval(() => void this.periodic('clock', () => this.client.resyncClock()), 30_000));
    this.timers.push(window.setInterval(() => void this.periodic('balance', () => this.fetchBalance()), 30_000));
    this.timers.push(window.setInterval(() => void this.periodic('positions', () => this.fetchAllPositions()), 30_000));
    // Backstop: order lifecycle is push-driven by the user_orders WS channel; this re-seeds from REST on drops.
    this.timers.push(window.setInterval(() => void this.periodic('orders', () => this.refreshAllOrders()), 120_000));
    this.timers.push(window.setInterval(() => void this.periodic('settlements', () => this.fetchAllSettlements()), 300_000));
    this.timers.push(window.setInterval(() => void this.refreshDeadman(), 5_000));
    this.markPnlDirty();
  }

  stop(): void {
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
    for (const t of this.pollTimers.values()) clearInterval(t);
    this.pollTimers.clear();
    this.ws.stop();
  }

  private async periodic(key: string, fn: () => Promise<unknown>): Promise<void> {
    if (this.singleFlight.has(key)) return; // coalesce overlapping refreshes
    const p = (async () => {
      try {
        await fn();
      } catch (e) {
        this.log('warn', `${key}: ${(e as Error).message}`);
      } finally {
        this.singleFlight.delete(key);
      }
    })();
    this.singleFlight.set(key, p);
    await p;
  }

  subscribe(ticker: string): void {
    this.tickerRefs.set(ticker, (this.tickerRefs.get(ticker) ?? 0) + 1);
    if (!this.subscribed.has(ticker)) void this.activateTicker(ticker);
    else this.markDirty(ticker);
  }

  private async activateTicker(ticker: string): Promise<void> {
    this.subscribed.add(ticker);
    this.states.set(ticker, newMarketState());
    this.ws.subscribeTickers([ticker]);
    this.pollTimers.set(ticker, window.setInterval(() => void this.pollTicker(ticker), 2_000));
    // Market status is polled: the market-data WS has no lifecycle channel, and can_close_early breaks close_time.
    this.infoTimers.set(ticker, window.setInterval(() => void this.fetchMarketInfo(ticker), 20_000));
    void this.fetchAllPositions();
    void this.fetchOpenOrders(ticker);
    void this.fetchMarketInfo(ticker);
    void this.pollTicker(ticker); // prime the book before the first delta lands
  }

  /** Decrement the ref count; at zero, drop all per-ticker state. */
  releaseTicker(ticker: string): number {
    const refs = Math.max(0, (this.tickerRefs.get(ticker) ?? 0) - 1);
    this.tickerRefs.set(ticker, refs);
    if (refs === 0) {
      this.subscribed.delete(ticker);
      const timer = this.pollTimers.get(ticker);
      if (timer !== undefined) {
        clearInterval(timer);
        this.pollTimers.delete(ticker);
      }
      const info = this.infoTimers.get(ticker);
      if (info !== undefined) {
        clearInterval(info);
        this.infoTimers.delete(ticker);
      }
      const st = this.states.get(ticker);
      if (st) {
        for (const oid of st.myOrders.keys()) {
          this.orderIndex.delete(oid);
          this.orderAccounts.delete(oid);
        }
      }
      this.states.delete(ticker);
      this.tickerRefs.delete(ticker);
    }
    return refs;
  }

  /** Grace window: don't drop a locally-placed order this young if REST hasn't caught up to it yet. */
  private readonly RECONCILE_GRACE_MS = 5_000;

  async fetchOpenOrders(ticker: string): Promise<void> {
    try {
      const orders = await this.client.getOpenOrders(ticker, 'resting');
      const state = this.states.get(ticker);
      if (!state) return;
      this.reconcileOrders(ticker, state, orders);
      this.markDirty(ticker);
      this.markPnlDirty();
      if (orders.length) void this.fetchQueuePositions(ticker);
      // Bracket hedge catch-up: covers fills that landed before arming.
      for (const [eid, b] of this.brackets) {
        if (b.ticker === ticker) void this.maybeHedge(eid);
      }
    } catch (e) {
      this.log('warn', `orders ${ticker}: ${(e as Error).message}`);
    }
  }

  /** Event-style reconciliation rather than wholesale replace; preserves estAhead/account_id/lifecycle/local_ts. */
  private reconcileOrders(ticker: string, state: MarketState, restOrders: any[]): void {
    const seen = new Set<string>();
    for (const od of restOrders) {
      const oid: string = od.order_id ?? '';
      if (!oid) continue;
      seen.add(oid);
      const yesMc = dollarsToMc(od.yes_price_dollars);
      const noMc = dollarsToMc(od.no_price_dollars);
      const status: string = od.status ?? 'resting';
      const existing = state.myOrders.get(oid);
      if (existing) {
        existing.status = status;
        // Don't let a stale REST 'partially_filled' downgrade a locally-'filled' order.
        if (status === 'partially_filled' && existing.lifecycle_state !== 'filled') {
          existing.lifecycle_state = 'partially_filled';
        }
        existing.remaining_mq = countToMq(od.remaining_count_fp) ?? existing.remaining_mq;
        existing.queue_position = od.queue_position ?? existing.queue_position;
        if (yesMc !== null) existing.yes_price_mc = yesMc;
        if (noMc !== null) existing.no_price_mc = noMc;
      } else {
        const coid: string = od.client_order_id ?? '';
        const source = this.brackets.has(oid) ? 'bracket'
          : this.bracketHedges.has(oid) ? 'bracket-hedge'
            : 'reconciled';
        state.myOrders.set(oid, {
          order_id: oid,
          client_order_id: coid,
          ticker,
          status,
          side: (od.outcome_side ?? od.side ?? '') as 'yes' | 'no' | '',
          yes_price_mc: yesMc,
          no_price_mc: noMc,
          remaining_mq: countToMq(od.remaining_count_fp) ?? 0,
          created_time: od.created_time,
          queue_position: od.queue_position ?? null,
          source,
          account_id: accountFromCoid(coid) ?? MOBILE_ID,
          lifecycle_state: 'open',
          local_ts: performance.now(),
          orig_mq: null,
        });
      }
      this.orderIndex.set(oid, ticker);
      const acc = state.myOrders.get(oid)!.account_id;
      if (acc) this.orderAccounts.set(oid, acc);
      // Seed estAhead for newly-seen orders; REST queue_position corrects shortly.
      if (!state.estAhead.has(oid)) this.seedQueue(state, oid);
    }

    const now = performance.now();
    for (const [oid, o] of [...state.myOrders]) {
      if (seen.has(oid)) continue;
      if (now - o.local_ts < this.RECONCILE_GRACE_MS) continue; // REST may just be lagging
      state.myOrders.delete(oid);
      state.estAhead.delete(oid);
      this.orderIndex.delete(oid);
      this.orderAccounts.delete(oid);
    }
  }

  /** Do we have a book for this market yet? */
  private bookLoaded(state: MarketState): boolean {
    return state.yesBids.size > 0 || state.noBids.size > 0;
  }

  /** Seed one order's queue estimate from the size resting at its level; refuses to guess without a book. */
  private seedQueue(state: MarketState, oid: string): void {
    const o = state.myOrders.get(oid);
    if (!o || !this.bookLoaded(state)) return;
    state.estAhead.set(oid, Math.max(0, this.levelQtyFor(state, o) - o.remaining_mq));
  }

  /** Seed any order still without an estimate. Called when a book arrives. */
  private seedMissingQueues(state: MarketState): void {
    for (const oid of state.myOrders.keys()) {
      if (!state.estAhead.has(oid)) this.seedQueue(state, oid);
    }
  }

  private levelQtyFor(state: MarketState, o: Order): MQ {
    if (o.side === 'yes' && o.yes_price_mc !== null) return state.yesBids.get(o.yes_price_mc) ?? 0;
    if (o.side === 'no' && o.no_price_mc !== null) return state.noBids.get(o.no_price_mc) ?? 0;
    return 0;
  }

  /** Debounced queue-position refresh: coalesces per-order requests into one fetch per ticker per window. */
  private requestQueuePositions(ticker: string, orderIds: string[]): void {
    const pend = this.queueFetchPending.get(ticker);
    if (pend) {
      for (const id of orderIds) pend.add(id);
      return;
    }
    this.queueFetchPending.set(ticker, new Set(orderIds));
    window.setTimeout(() => {
      const ids = this.queueFetchPending.get(ticker);
      this.queueFetchPending.delete(ticker);
      if (ids?.size) void this.fetchQueuePositions(ticker, [...ids]);
    }, this.QUEUE_FETCH_DEBOUNCE_MS);
  }

  async fetchQueuePositions(ticker: string, orderIds?: string[]): Promise<void> {
    const state = this.states.get(ticker);
    if (!state) return;
    const ids = orderIds ?? [...state.myOrders.values()]
      .filter((o) => (ACTIVE_STATUSES as readonly string[]).includes(o.status))
      .map((o) => o.order_id);
    if (!ids.length) return;

    const apply = (oid: string, val: number): boolean => {
      const st = this.states.get(ticker);
      const o = st?.myOrders.get(oid);
      if (!st || !o) return false;
      o.queue_position = Math.round(val);
      st.estAhead.set(oid, Math.round(val * MQ_PER_CONTRACT));
      return true;
    };

    // One bulk request for the whole market instead of one per order (the per-order read costs 2 tokens each).
    let applied = 0;
    try {
      for (const row of await this.client.getQueuePositions([ticker])) {
        if (apply(row.orderId, row.ahead)) applied++;
      }
    } catch (e) {
      this.log('warn', `bulk queue positions for ${ticker}: ${(e as Error).message}`);
    }

    // An empty answer is normal (nothing resting); only fall back when we know orders existed and got none back.
    if (!applied && ids.length) {
      const chunkSize = 8;
      for (let i = 0; i < ids.length; i += chunkSize) {
        await Promise.all(ids.slice(i, i + chunkSize).map(async (oid) => {
          try {
            const val = await this.client.getQueuePosition(oid);
            if (val !== null) apply(oid, val);
          } catch {
            /* a vanished order 404s here; the reconciler will drop it */
          }
        }));
      }
    }
    if (this.states.has(ticker)) this.markDirty(ticker);
  }

  /** Terminal states never revert, so anything polling for change can stop. */
  private static isTerminal(status: string): boolean {
    return ['settled', 'finalized', 'resolved', 'determined'].includes(status);
  }

  /** Stop the per-ticker REST polling once a market is settled; its book is empty and stays empty. */
  private stopPolling(ticker: string): void {
    for (const map of [this.pollTimers, this.infoTimers]) {
      const t = map.get(ticker);
      if (t !== undefined) {
        clearInterval(t);
        map.delete(ticker);
      }
    }
  }

  async fetchMarketInfo(ticker: string): Promise<void> {
    try {
      const mkt = await this.client.getMarket(ticker);
      const state = this.states.get(ticker);
      if (!state) return;
      state.priceLevelStructure = mkt.price_level_structure ?? 'tapered_deci_cent';
      state.priceRanges = mkt.price_ranges ?? [];
      // REST is authoritative for volume; reset the optimistic WS delta so the two can't double-count.
      state.volumeRest = Math.round(Number(mkt.volume_fp ?? mkt.volume ?? 0));
      state.volumeWs = 0;
      const prev = state.marketStatus;
      state.marketStatus = mkt.status ?? 'open';
      state.marketResult = String(mkt.result ?? '').toLowerCase();
      if (prev !== state.marketStatus) {
        this.log('info', `${ticker}: market ${prev} → ${state.marketStatus}`
          + (state.marketResult ? ` (${state.marketResult})` : ''));
      }
      if (Engine.isTerminal(state.marketStatus)) {
        // A merely `closed` market can still become settled with a result, so only stop once terminal.
        this.stopPolling(ticker);
        if (prev !== state.marketStatus) {
          this.log('info', `${ticker}: settled — stopped polling its book`);
        }
      }
      this.markDirty(ticker);
    } catch (e) {
      this.log('warn', `info ${ticker}: ${(e as Error).message}`);
    }
  }

  /** REST orderbook fallback for when the socket isn't open, is stale, or has never delivered for this ticker. */
  private async pollTicker(ticker: string): Promise<void> {
    const state = this.states.get(ticker);
    if (!state) return;
    // Covers an in-flight tick that was already scheduled when the market settled.
    if (Engine.isTerminal(state.marketStatus)) return;
    const socketDelivering = this.wsStatus === 'open' && !this.stale && state.lastWsTs > 0;
    if (socketDelivering) return;
    try {
      const ob = await this.client.getOrderbook(ticker);
      const st = this.states.get(ticker);
      if (!st) return;
      st.yesBids = levelsToMap(ob.yes_dollars);
      st.noBids = levelsToMap(ob.no_dollars);
      this.recomputeTop(st);
      this.seedMissingQueues(st);
      this.clampAllQueues(st);
      this.markDirty(ticker);
    } catch (e) {
      this.log('warn', `poll ${ticker}: ${(e as Error).message}`);
    }
  }

  /** Exchange truth for one order, read back by id. A 404 means the exchange does not know it: null. */
  async orderFill(orderId: string): Promise<{ filledMq: MQ; status: string } | null> {
    let od: any;
    try {
      od = await this.client.getOrder(orderId);
    } catch (e) {
      if (e instanceof KalshiAPIError && e.status === 404) return null;
      throw e;
    }
    return { filledMq: countToMq(od.fill_count_fp ?? od.fill_count) ?? 0, status: String(od.status ?? '') };
  }

  shardBalances(): Promise<{ totalCash: Cash; shards: ShardBalance[] }> {
    return this.client.getBalanceBreakdown();
  }

  async transferShards(fromShard: number, toShard: number, amountCash: Cash): Promise<string> {
    const before = await this.client.getBalanceBreakdown().catch(() => null);
    let id: string;
    try {
      id = await this.client.transferBetweenShards(fromShard, toShard, amountCash);
    } catch (e) {
      const err = e as KalshiAPIError;
      journal({ venue: 'kalshi', kind: 'transfer', bad: true, msg: `transfer $${(amountCash / 1e6).toFixed(2)} shard ${fromShard} → ${toShard} REFUSED: ${err.text || err.message}`, data: { fromShard, toShard, amountCash, before } });
      throw e;
    }
    journal({ venue: 'kalshi', kind: 'transfer', msg: `transfer $${(amountCash / 1e6).toFixed(2)} shard ${fromShard} → ${toShard} accepted · ${id}`, data: { fromShard, toShard, amountCash, transferId: id, before } });
    this.log('info', `moved $${(amountCash / 1e6).toFixed(2)} from shard ${fromShard} to shard ${toShard} (${id})`);
    void this.fetchBalance();
    return id;
  }

  targetAllocation(): Promise<ShardAllocation[]> {
    return this.client.getTargetAllocation();
  }

  async setTargetAllocation(allocations: ShardAllocation[]): Promise<void> {
    await this.client.setTargetAllocation(allocations);
    journal({ venue: 'kalshi', kind: 'transfer', msg: allocations.length ? `auto-rebalance set: ${allocations.map((a) => `shard ${a.exchangeIndex} ${a.percent}%`).join(', ')}` : 'auto-rebalance turned off', data: { allocations } });
  }

  async fetchBalance(): Promise<void> {
    try {
      this.balanceCash = await this.client.getBalance();
      this.markPnlDirty();
    } catch (e) {
      this.log('warn', `balance: ${(e as Error).message}`);
    }
  }

  /** Single-flight position refresh; concurrent callers set a queued flag so the in-flight call loops once more. */
  async fetchAllPositions(): Promise<void> {
    if (this.posLock) {
      this.posQueued = true;
      return this.posLock;
    }
    this.posLock = (async () => {
      try {
        for (;;) {
          this.posQueued = false;
          const pendingSnapshot = new Map<string, number>();
          for (const [t, s] of this.states) pendingSnapshot.set(t, s.pendingDeltas);
          let raw: any[];
          try {
            raw = await this.client.getPositions();
          } catch (e) {
            this.log('warn', `positions: ${(e as Error).message}`);
            break;
          }
          this.positions = raw.filter((p) =>
            (countToMq(p.position_fp) ?? 0) !== 0 || (dollarsToCash(p.realized_pnl_dollars) ?? 0) !== 0);
          const byTicker = new Map<string, any>(this.positions.map((p) => [p.ticker, p]));
          for (const [ticker, state] of this.states) {
            const saved = pendingSnapshot.get(ticker) ?? 0;
            const pos = byTicker.get(ticker);
            if (pos) {
              state.realizedCash = dollarsToCash(pos.realized_pnl_dollars) ?? 0;
              state.feesCash = dollarsToCash(pos.fees_paid_dollars) ?? 0;
              if (state.pendingDeltas === saved) {
                state.positionMq = countToMq(pos.position_fp) ?? 0;
                // Cost basis is market_exposure_dollars (current open position), not gross total_traded_dollars.
                const exposure = dollarsToCash(pos.market_exposure_dollars) ?? 0;
                const absPos = Math.abs(state.positionMq);
                if (absPos > 0 && exposure > 0) {
                  const costMc = exposure / absPos;
                  state.avgCostMc = state.positionMq < 0 ? MC_SETTLE - costMc : costMc;
                } else {
                  state.avgCostMc = 0;
                }
              }
              state.pendingDeltas = Math.max(0, state.pendingDeltas - saved);
            } else {
              if (state.pendingDeltas === saved) {
                state.positionMq = 0;
                state.avgCostMc = 0;
              }
              state.pendingDeltas = Math.max(0, state.pendingDeltas - saved);
            }
            this.markDirty(ticker);
          }
          this.markPnlDirty();
          if (!this.posQueued) break;
        }
      } finally {
        this.posLock = null;
      }
    })();
    return this.posLock;
  }

  async fetchAllSettlements(): Promise<void> {
    try {
      this.settlements = await this.client.getSettlements();
      this.markPnlDirty();
    } catch (e) {
      this.log('warn', `settlements: ${(e as Error).message}`);
    }
  }

  /** Refresh the global resting-order list (every ticker) so Orders Out reflects more than just open panels. */
  async fetchAllOrders(): Promise<void> {
    try {
      const orders = await this.client.getAllOpenOrders('resting');
      this.allResting = new Map(orders
        .filter((o) => o.order_id)
        .map((o) => [o.order_id, {
          ticker: o.ticker ?? '',
          order_id: o.order_id,
          client_order_id: o.client_order_id ?? '',
          side: o.outcome_side ?? o.side ?? '',
        }]));
      this.markPnlDirty();
    } catch (e) {
      this.log('warn', `all-orders: ${(e as Error).message}`);
    }
  }

  private async refreshAllOrders(): Promise<void> {
    await this.fetchAllOrders();
    for (const ticker of [...this.subscribed]) await this.fetchOpenOrders(ticker);
  }

  /** Re-read one book from REST, replacing whatever deltas left behind; throttled per ticker to 1.5s. */
  private async resyncBook(ticker: string, reason: string): Promise<void> {
    const now = performance.now();
    if (now - (this.lastResync.get(ticker) ?? -Infinity) < 1_500) return;
    this.lastResync.set(ticker, now);
    try {
      const ob = await this.client.getOrderbook(ticker);
      const st = this.states.get(ticker);
      if (!st) return;
      st.yesBids = levelsToMap(ob.yes_dollars);
      st.noBids = levelsToMap(ob.no_dollars);
      this.recomputeTop(st);
      this.seedMissingQueues(st);
      this.clampAllQueues(st);
      this.markDirty(ticker);
      this.log('info', `${ticker}: book resynced (${reason})`);
    } catch (e) {
      this.log('warn', `${ticker}: resync failed (${reason}): ${(e as Error).message}`);
    }
  }

  private recomputeTop(s: MarketState): void {
    s.bestYesBid = maxKey(s.yesBids);
    s.bestNoBid = maxKey(s.noBids);
    s.bestYesAsk = s.bestNoBid !== null ? MC_SETTLE - s.bestNoBid : null;
    s.bestNoAsk = s.bestYesBid !== null ? MC_SETTLE - s.bestYesBid : null;
    // A matched exchange cannot show a bid at or above the ask; if the local book says so, the book is wrong.
    s.crossed = s.bestYesBid !== null && s.bestYesAsk !== null && s.bestYesBid >= s.bestYesAsk;
    s.midYesDeci = (s.bestYesBid !== null && s.bestYesAsk !== null && !s.crossed)
      ? (s.bestYesBid + s.bestYesAsk) * 5 // *10/2, kept integral
      : null;
    let open = 0, bids = 0, asks = 0;
    for (const o of s.myOrders.values()) {
      if (!(ACTIVE_STATUSES as readonly string[]).includes(o.status)) continue;
      open++;
      if (o.side === 'yes') bids++;
      else if (o.side === 'no') asks++;
    }
    s.openOrders = open;
    s.bidOrders = bids;
    s.askOrders = asks;
  }

  /** Cap estAhead at (level - our size); skips very-young orders whose own add-delta hasn't landed yet. */
  private clampOrderQueue(state: MarketState, o: Order): void {
    if (performance.now() - o.local_ts < 2_000) return;
    const level = this.levelQtyFor(state, o);
    if (o.side !== 'yes' && o.side !== 'no') return;
    const cap = Math.max(0, level - o.remaining_mq);
    const cur = state.estAhead.get(o.order_id);
    if (cur !== undefined && cur > cap) state.estAhead.set(o.order_id, cap);
  }

  private clampAllQueues(state: MarketState): void {
    for (const o of state.myOrders.values()) {
      if ((ACTIVE_STATUSES as readonly string[]).includes(o.status)) this.clampOrderQueue(state, o);
    }
  }

  private async handleWsMsg(msg: any): Promise<void> {
    const t = msg.type;
    const m = msg.msg ?? {};

    // Gap detection: Kalshi numbers each frame per subscription; a break means the local book has diverged.
    if (typeof msg.seq === 'number' && typeof msg.sid === 'number') {
      const prev = this.wsSeq.get(msg.sid);
      this.wsSeq.set(msg.sid, msg.seq);
      if (prev !== undefined && msg.seq !== prev + 1) {
        this.log('warn', `ws seq gap on sid ${msg.sid}: ${prev} → ${msg.seq} — resyncing books`);
        for (const tk of this.subscribed) void this.resyncBook(tk, 'seq gap');
      }
    }

    if (t === 'error') {
      this.log('error', `ws error frame: ${JSON.stringify(msg).slice(0, 300)}`);
      return;
    }
    if (t === 'user_order') {
      this.applyUserOrder(m);
      return;
    }
    if (t === 'orderbook_snapshot') {
      const ticker: string = m.market_ticker ?? '';
      const state = this.states.get(ticker);
      if (!state) return;
      state.yesBids = levelsToMap(m.yes_dollars_fp);
      state.noBids = levelsToMap(m.no_dollars_fp);
      this.recomputeTop(state);
      // Orders that arrived before this book could not be measured against a level; now they can.
      this.seedMissingQueues(state);
      this.clampAllQueues(state);
      if (state.crossed) void this.resyncBook(ticker, 'crossed after snapshot');
      state.lastWsTs = performance.now();
      if (this.stale) {
        this.stale = false;
        this.emit('status', { ws: this.wsStatus, stale: false });
      }
      this.markDirty(ticker);
      return;
    }
    if (t === 'orderbook_delta') {
      const ticker: string = m.market_ticker ?? '';
      const state = this.states.get(ticker);
      if (!state) return;
      const side: string = m.side;
      const price = dollarsToMc(m.price_dollars);
      const delta = countToMq(m.delta_fp);
      if (price === null || delta === null) return;
      const book = side === 'yes' ? state.yesBids : state.noBids;
      const next = Math.max((book.get(price) ?? 0) + delta, 0);
      if (next === 0) book.delete(price);
      else book.set(price, next);
      this.recomputeTop(state);
      // A shrinking level can't leave more contracts "ahead" of us than now rest at it.
      if (delta < 0) {
        for (const o of state.myOrders.values()) {
          if (!(ACTIVE_STATUSES as readonly string[]).includes(o.status)) continue;
          if ((side === 'yes' && o.yes_price_mc === price) || (side === 'no' && o.no_price_mc === price)) {
            this.clampOrderQueue(state, o);
          }
        }
      }
      if (state.crossed) void this.resyncBook(ticker, 'crossed book');
      state.lastWsTs = performance.now();
      if (this.stale) {
        this.stale = false;
        this.emit('status', { ws: this.wsStatus, stale: false });
      }
      this.markDirty(ticker);
      return;
    }
    if (t === 'trade') {
      const ticker: string = m.market_ticker ?? '';
      const state = this.states.get(ticker);
      if (!state) return;
      const mq = countToMq(m.count_fp ?? m.count) ?? 0;
      state.volumeWs += Math.round(mq / MQ_PER_CONTRACT); // optimistic; REST rebases it
      const yesMc = dollarsToMc(m.yes_price_dollars);
      if (yesMc !== null && mq > 0) {
        // A public trade consumes from the front of the queue; our own fill (handleFill) pre-adds it back
        // so the two frames net to zero regardless of arrival order.
        const noMc = MC_SETTLE - yesMc;
        for (const [oid, o] of state.myOrders) {
          if (!(ACTIVE_STATUSES as readonly string[]).includes(o.status)) continue;
          const atLevel = (o.side === 'yes' && o.yes_price_mc === yesMc)
            || (o.side === 'no' && o.no_price_mc === noMc);
          const cur = state.estAhead.get(oid);
          if (atLevel && cur !== undefined) state.estAhead.set(oid, Math.max(0, cur - mq));
        }
      }
      if (yesMc !== null && mq > 0) {
        // The frame has no timestamp of its own; arrival instant is used, good enough for bar bucketing only.
        this.emit('trade', {
          ticker,
          trade: { t: Date.now(), priceMc: yesMc, mq, taker: m.taker_side === 'no' ? 'no' : 'yes' },
        });
      }
      this.markDirty(ticker);
      return;
    }
    if (t === 'fill') {
      await this.handleFill(m);
    }
  }

  /** WS-pushed order lifecycle: authoritative for status/remaining; `fill` stays the accounting source. */
  private applyUserOrder(m: any): void {
    const oid: string = m.order_id ?? '';
    if (!oid) return;
    const ticker: string = m.ticker ?? '';
    const status = String(m.status ?? '').toLowerCase();
    // The V2 feed quotes the YES leg only; the real bid(yes)/ask(no) side is in outcome_side.
    const side = (m.outcome_side ?? m.side ?? '') as 'yes' | 'no' | '';
    const coid: string = m.client_order_id ?? '';
    const acc = this.orderAccounts.get(oid) ?? accountFromCoid(coid) ?? MOBILE_ID;
    const active = (WS_ORDER_ACTIVE as readonly string[]).includes(status);
    const yesMc = dollarsToMc(m.yes_price_dollars);
    const remMq = countToMq(m.remaining_count_fp);

    if (active) {
      this.allResting.set(oid, { ticker, order_id: oid, client_order_id: coid, side });
      this.orderIndex.set(oid, ticker);
      if (acc) this.orderAccounts.set(oid, acc);
    } else {
      this.allResting.delete(oid);
      this.orderIndex.delete(oid);
      this.orderAccounts.delete(oid);
    }

    const state = this.states.get(ticker);
    if (state) {
      if (active) {
        let o = state.myOrders.get(oid);
        if (!o) {
          const source = this.brackets.has(oid) ? 'bracket'
            : this.bracketHedges.has(oid) ? 'bracket-hedge'
              : coid.startsWith('ui-') ? 'manual'
                : 'external';
          o = {
            order_id: oid,
            client_order_id: coid,
            ticker,
            status,
            side,
            yes_price_mc: side === 'yes' ? yesMc : null,
            no_price_mc: side === 'no' && yesMc !== null ? MC_SETTLE - yesMc : null,
            remaining_mq: remMq ?? 0,
            created_time: m.created_time,
            queue_position: null,
            source,
            account_id: acc,
            lifecycle_state: 'open',
            local_ts: performance.now(),
            orig_mq: null,
          };
          state.myOrders.set(oid, o);
          state.estAhead.set(oid, Math.max(0, this.levelQtyFor(state, o) - o.remaining_mq));
          this.requestQueuePositions(ticker, [oid]);
        } else {
          o.status = status;
          if (remMq !== null) o.remaining_mq = remMq;
          if ((status === 'partially_filled' || status === 'partial_fill') && o.lifecycle_state !== 'filled') {
            o.lifecycle_state = 'partially_filled';
          }
          if (yesMc !== null) {
            if (side === 'yes') o.yes_price_mc = yesMc;
            else if (side === 'no') o.no_price_mc = MC_SETTLE - yesMc;
          }
        }
      } else {
        state.myOrders.delete(oid);
        state.estAhead.delete(oid);
      }
      this.recomputeTop(state);
      this.markDirty(ticker);
    }
    this.markPnlDirty();
  }

  private async handleFill(m: any): Promise<void> {
    const ticker: string = m.market_ticker ?? '';
    const mq = countToMq(m.count_fp ?? m.count) ?? 0;
    const ypd = m.yes_price_dollars;
    if (mq === 0 || !ypd) return;
    const yesPriceMc = dollarsToMc(ypd);
    if (yesPriceMc === null) return;
    const orderId: string = m.order_id ?? '';
    const coid: string = m.client_order_id ?? '';
    const ts = Number(m.ts ?? 0) || Math.floor(Date.now() / 1000);
    const feeCash = dollarsToCash(m.fee ?? m.fee_dollars) ?? 0;
    const isTaker = Boolean(m.is_taker);

    // outcome_side is canonical and already buy-normalised (buy-yes == sell-no); action/side is a legacy fallback.
    let side: 'yes' | 'no';
    const oside = String(m.outcome_side ?? '').toLowerCase();
    if (oside === 'yes' || oside === 'no') {
      side = oside;
    } else {
      side = (m.side ?? 'yes') as 'yes' | 'no';
      if (String(m.action ?? 'buy').toLowerCase() === 'sell') side = side === 'yes' ? 'no' : 'yes';
    }

    // Dedup id: Kalshi sends trade_id in practice; the fallback is deterministic across a WS replay.
    const fid: string = m.trade_id ?? m.fill_id
      ?? `${orderId}:${ts}:${mq}:${yesPriceMc}:${isTaker ? 1 : 0}`;

    const accountId = this.attributeAccount(orderId, coid, fid);
    const row: FillRow = {
      fill_id: fid, ts, ticker, side, mq, yes_price_mc: yesPriceMc,
      fee_cash: feeCash, is_taker: isTaker, order_id: orderId,
      client_order_id: coid, account_id: accountId,
    };
    const written = await appendFill(row);
    if (written) {
      const acct = this.accounts.get(accountId);
      if (acct) this.accounts.applyToPositions(acct, row);
      this.recordMarker(ticker, accountId, yesPriceMc, side);
      this.markPnlDirty();
      this.emit('fill', {
        type: 'fill_event', ticker, account_id: accountId, ts, side, mq,
        yes_price_mc: yesPriceMc, is_taker: isTaker, order_id: orderId,
      });
      journal({
        venue: 'kalshi', kind: 'fill', ticker, orderId,
        msg: `FILL ${side} ${mq / MQ_PER_CONTRACT} @ ${yesPriceMc / 10}¢ ${ticker} ${isTaker ? 'taker' : 'maker'} · order ${orderId} · acct ${accountId}`,
        data: row,
      });
    }

    const state = this.states.get(ticker);
    if (!state || !written) {
      // Duplicate (WS replay): still re-reconcile from REST to be safe.
      void this.fetchAllPositions();
      return;
    }

    const o = state.myOrders.get(orderId);
    if (o) {
      const rem = Math.max(0, o.remaining_mq - mq);
      o.remaining_mq = rem;
      o.lifecycle_state = rem <= 0 ? 'filled' : 'partially_filled';
      if (rem <= 0) o.status = 'filled';
      // Pre-add mq back to estAhead so the matching public `trade` frame's decrement nets to zero.
      const cur = state.estAhead.get(orderId);
      if (cur !== undefined) state.estAhead.set(orderId, cur + mq);
    }

    const bracket = this.brackets.get(orderId);
    if (bracket) {
      bracket.entry_filled_mq += mq;
      await this.saveBrackets();
      void this.maybeHedge(orderId);
    }

    // Optimistic position update, guarded by pendingDeltas so a stale REST snapshot can't roll it back.
    state.pendingDeltas++;
    state.positionMq += side === 'yes' ? mq : -mq;
    this.recomputeTop(state);
    void this.fetchAllPositions();
    this.markDirty(ticker);
  }

  /** Resolve a fill to an account: manual override wins, then the live order map, then the coid, else mobile. */
  private attributeAccount(orderId: string, coid: string, fillId?: string): string {
    const manual = fillId ? this.fillOverrides.get(fillId) : undefined;
    if (manual && this.accounts.get(manual)) return manual;
    const aid = this.orderAccounts.get(orderId) ?? accountFromCoid(coid);
    if (aid && this.accounts.get(aid)) return aid;
    return MOBILE_ID;
  }

  private recordMarker(ticker: string, accountId: string, levelMc: MC, side: 'yes' | 'no'): void {
    this.rollMarkers();
    let byAcct = this.fillMarkers.get(ticker);
    if (!byAcct) {
      byAcct = new Map();
      this.fillMarkers.set(ticker, byAcct);
    }
    let levels = byAcct.get(accountId);
    if (!levels) {
      levels = new Map();
      byAcct.set(accountId, levels);
    }
    const cell = levels.get(levelMc) ?? [0, 0];
    cell[side === 'yes' ? 0 : 1]++;
    levels.set(levelMc, cell);
  }

  private rollMarkers(): void {
    const today = ptDayStr();
    if (today !== this.markersDay) {
      this.markersDay = today;
      void this.seedMarkers(); // rebuild from the ledger (fills since midnight PT)
    }
  }

  /** (Re)build today's ladder fill markers from the ledger; survives a reload and resets at midnight PT. */
  async seedMarkers(): Promise<void> {
    const since = ptDayStartEpoch();
    this.fillMarkers.clear();
    for (const f of await allFills()) {
      if ((f.ts || 0) < since) continue;
      if (!f.ticker || !f.yes_price_mc) continue;
      let byAcct = this.fillMarkers.get(f.ticker);
      if (!byAcct) {
        byAcct = new Map();
        this.fillMarkers.set(f.ticker, byAcct);
      }
      let levels = byAcct.get(f.account_id);
      if (!levels) {
        levels = new Map();
        byAcct.set(f.account_id, levels);
      }
      const cell = levels.get(f.yes_price_mc) ?? [0, 0];
      cell[f.side === 'yes' ? 0 : 1]++;
      levels.set(f.yes_price_mc, cell);
    }
  }

  /** Is a YES-equivalent price on the market's valid grid? `price_ranges` is the source of truth, not the label. */
  static priceOnGrid(priceMc: MC, ranges: { start: string; end: string; step: string }[]): boolean {
    if (!ranges.length) return true;
    for (const r of ranges) {
      const s = dollarsToMc(r.start);
      const e = dollarsToMc(r.end);
      const st = dollarsToMc(r.step);
      if (s === null || e === null || !st) continue;
      if (s <= priceMc && priceMc <= e && (priceMc - s) % st === 0) return true;
    }
    return false;
  }

  async placeOrder(args: PlaceArgs): Promise<PlaceResult> {
    const t0 = performance.now();
    const res = await this.doPlace(args);
    journal({
      venue: 'kalshi', kind: 'place', tag: args.tag, ticker: args.ticker, orderId: res.ok ? res.order.order_id ?? undefined : undefined, bad: !res.ok,
      msg: `place ${args.mode} ${args.side} ${args.qtyMq / MQ_PER_CONTRACT} @ ${args.priceMc / 10}¢ ${args.ticker} → ${res.ok ? `${res.order.status} ${res.order.order_id ?? ''}` : `REFUSED: ${res.error}`} (${Math.round(performance.now() - t0)}ms)`,
      data: { args, result: res },
    });
    return res;
  }

  private async doPlace(args: PlaceArgs): Promise<PlaceResult> {
    const { ticker, side, priceMc, qtyMq, mode, accountId } = args;
    if (!this.accounts.get(accountId)) return { ok: false, error: `unknown account: ${accountId}` };
    if (qtyMq <= 0) return { ok: false, error: 'quantity must be > 0' };

    const yesPriceMc = side === 'yes' ? priceMc : MC_SETTLE - priceMc;
    if (yesPriceMc < 1 || yesPriceMc > 999) return { ok: false, error: `price out of range: ${yesPriceMc} mc` };

    const state = this.states.get(ticker);
    if (state && !Engine.priceOnGrid(yesPriceMc, state.priceRanges)) {
      return {
        ok: false,
        error: `price ${(yesPriceMc / 10).toFixed(1)}¢ (YES) is not on this market's price grid — this band only allows coarser ticks`,
      };
    }

    const clientOrderId = coidFor(accountId);
    let od: NormalizedOrder;
    try {
      od = await this.client.placeOrder({
        ticker, side, countMq: qtyMq, yesPriceMc, clientOrderId, mode, tag: args.tag,
        // Maker-STP: crossing your own resting quote cancels the resting one instead of the new order dying.
        selfTradePreventionType: 'maker',
      });
    } catch (e) {
      const err = e as KalshiAPIError;
      this.log('error', `order ${ticker}: ${err.message}`);
      return { ok: false, error: err.text || err.message, status: err.status };
    }

    // A 200 with no order_id is not a placed order: it would be untracked, uncancellable, unhedgeable.
    if (!od.order_id) {
      this.log('error', `order ${ticker}: place returned no order_id`);
      return { ok: false, error: 'no order_id in response' };
    }

    const st = this.states.get(ticker);
    if (st) {
      const noPriceMc = od.yes_price_mc !== null ? MC_SETTLE - od.yes_price_mc : null;
      const o: Order = {
        order_id: od.order_id,
        client_order_id: od.client_order_id ?? clientOrderId,
        ticker,
        status: od.status ?? 'resting',
        side,
        yes_price_mc: od.yes_price_mc ?? yesPriceMc,
        no_price_mc: noPriceMc ?? MC_SETTLE - yesPriceMc,
        remaining_mq: od.remaining_mq ?? qtyMq,
        created_time: od.created_time,
        queue_position: od.queue_position ?? null,
        source: args.source ?? 'manual',
        account_id: accountId,
        lifecycle_state: 'open',
        local_ts: performance.now(),
        orig_mq: qtyMq,
      };
      st.myOrders.set(o.order_id, o);
      this.orderIndex.set(o.order_id, ticker);
      this.orderAccounts.set(o.order_id, accountId);
      st.estAhead.set(o.order_id, this.levelQtyFor(st, o));
      this.recomputeTop(st);
      void this.fetchQueuePositions(ticker, [o.order_id]);
      // Straight to the ladder; the 40ms flush tick is for feed frames, not something the user just did.
      this.dirtyTickers.delete(ticker);
      this.emit('snapshot', this.buildUiSnapshot(ticker));
      this.markPnlDirty(); // refresh Orders Out now, not on the slow loop
    }
    return { ok: true, order: od };
  }

  /** `filledMq` is set when the cancel found the order already closed: the exchange's fill count for it. */
  async cancelOrder(orderId: string, tag?: string): Promise<{ ok: boolean; error?: string; status?: number; filledMq?: MQ }> {
    const t0 = performance.now();
    const ticker = this.orderIndex.get(orderId);
    const res = await this.doCancel(orderId, ticker, tag);
    journal({
      venue: 'kalshi', kind: 'cancel', tag, ticker, orderId, bad: !res.ok,
      msg: `cancel ${orderId}${ticker ? ` on ${ticker}` : ' (ticker unknown — routes to shard 0)'} → ${res.ok ? 'ok' : `FAILED: ${res.error}`} (${Math.round(performance.now() - t0)}ms)`,
      data: { result: res },
    });
    return res;
  }

  private async doCancel(orderId: string, ticker: string | undefined, tag?: string): Promise<{ ok: boolean; error?: string; status?: number; filledMq?: MQ }> {
    // Ticker is needed to reach the exchange shard the order rests on (see KalshiClient.cancelOrder).
    let filledMq: MQ | undefined;
    try {
      try {
        await this.client.cancelOrder(orderId, ticker, tag);
      } catch (e) {
        // 404 = already gone (filled/cancelled elsewhere), so report success; 410 means the order still rests.
        const err = e as KalshiAPIError;
        if (err.status !== 404) throw e;
        // The client's read-back says how much filled — the arb engine hedges that before it re-quotes.
        const od = err.order as { fill_count_fp?: string; fill_count?: number } | undefined;
        if (od) filledMq = countToMq(od.fill_count_fp ?? od.fill_count) ?? undefined;
      }
      this.orderIndex.delete(orderId);
      this.orderAccounts.delete(orderId);
      this.allResting.delete(orderId);
      // Cancelling a bracket entry stops further hedging; resting hedges stay.
      if (this.brackets.delete(orderId)) await this.saveBrackets();
      this.bracketHedges.delete(orderId);
      const state = ticker ? this.states.get(ticker) : undefined;
      if (state && state.myOrders.delete(orderId)) {
        state.estAhead.delete(orderId);
        this.recomputeTop(state);
        this.dirtyTickers.delete(ticker!);
        this.emit('snapshot', this.buildUiSnapshot(ticker!));
      }
      this.markPnlDirty();
      return filledMq !== undefined ? { ok: true, filledMq } : { ok: true };
    } catch (e) {
      const err = e as KalshiAPIError;
      const detail = err.text || err.message;
      // Logged here too: several call sites (deadman, bracket teardown) fire cancels with no UI to report into.
      this.log('error', `CANCEL FAILED for ${orderId} — order may still be resting: ${detail}`);
      return { ok: false, error: detail, status: err.status };
    }
  }

  /** Cancel this ticker's resting orders; with an accountId, only that account's orders and brackets. */
  async cancelAllOrders(ticker: string, accountId?: string): Promise<{ ok: boolean; cancelled: number; failed: number; error?: string }> {
    const state = this.states.get(ticker);
    if (!state) return { ok: false, cancelled: 0, failed: 0, error: 'unknown ticker' };
    const mine = (acct: string | null | undefined) => !accountId || acct === accountId;

    // Disarm brackets first so no new hedge orders get placed mid-sweep.
    let changed = false;
    for (const [eid, b] of [...this.brackets]) {
      if (b.ticker === ticker && mine(b.account_id)) {
        this.brackets.delete(eid);
        this.hedgeLocks.delete(eid);
        changed = true;
      }
    }
    if (changed) await this.saveBrackets();

    // Bounded retry loop to catch orders placed mid-cancel (e.g. an in-flight hedge).
    let cancelled = 0;
    let failed = 0;
    let lastError = '';
    for (let round = 0; round < 4; round++) {
      const ids = [...state.myOrders.values()]
        .filter((o) => (ACTIVE_STATUSES as readonly string[]).includes(o.status) && mine(o.account_id))
        .map((o) => o.order_id);
      if (ids.length) {
        const results = await Promise.all(ids.map((oid) => this.cancelOrder(oid)));
        failed = results.filter((r) => !r.ok).length;
        cancelled += ids.length - failed;
        lastError = results.find((r) => !r.ok)?.error ?? lastError;

        // Every cancel in the round failed, so the next round will fail the same way; stop instead of retrying.
        if (failed === ids.length) {
          this.log('error', `cancel failed for all ${failed} order(s) on ${ticker}: ${lastError}`);
          break;
        }
      }
      try {
        const rest = await this.client.getOpenOrders(ticker, 'resting');
        this.reconcileOrders(ticker, state, rest);
      } catch {
        break;
      }
      const remaining = [...state.myOrders.values()]
        .some((o) => (ACTIVE_STATUSES as readonly string[]).includes(o.status) && mine(o.account_id));
      if (!remaining) break;
    }
    this.markDirty(ticker);
    this.markPnlDirty();
    return { ok: failed === 0, cancelled, failed, ...(failed ? { error: lastError } : {}) };
  }

  /** Cancel all resting orders at a YES price, scoped to ONE ticker only (never across the whole book). */
  async cancelLevel(ticker: string, yesPriceMc: MC, accountId?: string): Promise<{ ok: boolean; cancelled: number; failed: number; error?: string }> {
    const state = this.states.get(ticker);
    if (!state) return { ok: false, cancelled: 0, failed: 0, error: 'unknown ticker' };
    const noPriceMc = MC_SETTLE - yesPriceMc;
    const ids: string[] = [];
    for (const o of state.myOrders.values()) {
      if (!(ACTIVE_STATUSES as readonly string[]).includes(o.status)) continue;
      if (accountId && o.account_id !== accountId) continue;
      if (o.side === 'yes' && o.yes_price_mc === yesPriceMc) ids.push(o.order_id);
      else if (o.side === 'no' && o.no_price_mc === noPriceMc) ids.push(o.order_id);
    }
    if (!ids.length) return { ok: true, cancelled: 0, failed: 0 };
    const results = await Promise.all(ids.map((oid) => this.cancelOrder(oid)));
    const failed = results.filter((r) => !r.ok).length;
    return { ok: failed === 0, cancelled: ids.length - failed, failed };
  }

  private async loadBrackets(): Promise<void> {
    const stored = await getMeta<Record<string, Bracket>>('brackets', {});
    this.brackets = new Map(Object.entries(stored));
    this.bracketHedges.clear();
    for (const [eid, b] of this.brackets) {
      // Re-derive filled qty from the ledger: captures fills that landed while the tab was closed.
      b.entry_filled_mq = await this.ledgerFilledMq(b.account_id, eid);
      for (const hid of b.hedge_order_ids) this.bracketHedges.set(hid, eid);
    }
  }

  private async saveBrackets(): Promise<void> {
    await setMeta('brackets', Object.fromEntries(this.brackets));
  }

  /** Total filled qty for an entry order, summed from the ledger regardless of whether the order is still tracked. */
  private async ledgerFilledMq(accountId: string, entryOrderId: string): Promise<MQ> {
    if (!accountId) return 0;
    let total = 0;
    for (const f of await allFills()) {
      if (f.order_id === entryOrderId && f.account_id === accountId) total += f.mq;
    }
    return total;
  }

  /** Arm a bracket: as the entry fills, post hedge orders at hedgePriceMc until filled qty is fully hedged. */
  async armBracket(args: {
    ticker: string;
    entryOrderId: string;
    hedgeSide: 'yes' | 'no';
    hedgePriceMc: MC;
    accountId: string;
    entrySide?: 'yes' | 'no';
    entryQtyMq?: MQ;
  }): Promise<{ ok: boolean; error?: string }> {
    const { ticker, entryOrderId, hedgeSide, hedgePriceMc, accountId } = args;
    if (hedgeSide !== 'yes' && hedgeSide !== 'no') return { ok: false, error: 'bad hedge side' };
    if (hedgePriceMc < 1 || hedgePriceMc > 999) return { ok: false, error: 'hedge price out of range' };
    const state = this.states.get(ticker);
    const order = state?.myOrders.get(entryOrderId);
    const side = (order?.side || args.entrySide) as 'yes' | 'no' | undefined;
    const origMq = order?.orig_mq || args.entryQtyMq || 0;
    if ((side !== 'yes' && side !== 'no') || origMq <= 0) {
      return { ok: false, error: 'cannot determine entry side/qty' };
    }
    const acctId = order?.account_id || accountId;
    this.brackets.set(entryOrderId, {
      ticker,
      account_id: acctId,
      entry_side: side,
      hedge_side: hedgeSide,
      hedge_price_mc: hedgePriceMc,
      orig_mq: origMq,
      // Filled-so-far from the ledger: the entry may already have filled before arming.
      entry_filled_mq: await this.ledgerFilledMq(acctId, entryOrderId),
      hedged_mq: 0,
      hedge_order_ids: [],
    });
    if (order) order.source = 'bracket';
    await this.saveBrackets();
    this.markDirty(ticker);
    void this.maybeHedge(entryOrderId); // catch up qty filled between placement and arming
    return { ok: true };
  }

  /** Post hedge orders to match the entry's filled qty; serialised per entry so concurrent triggers can't double-hedge. */
  private async maybeHedge(entryOrderId: string): Promise<void> {
    const prev = this.hedgeLocks.get(entryOrderId) ?? Promise.resolve();
    const run = prev.then(() => this.hedgeOnce(entryOrderId));
    this.hedgeLocks.set(entryOrderId, run.catch(() => undefined));
    return run;
  }

  private async hedgeOnce(entryOrderId: string): Promise<void> {
    let br = this.brackets.get(entryOrderId);
    if (!br) return;

    // Don't try to hedge into a closed market — drop the now-stale bracket.
    const state = this.states.get(br.ticker);
    if (state && ['finalized', 'settled', 'resolved'].includes(state.marketStatus)) {
      this.brackets.delete(entryOrderId);
      this.hedgeLocks.delete(entryOrderId);
      await this.saveBrackets();
      return;
    }

    // Filled comes from the ledger, not the live order's remaining, so it works even after the order vanishes.
    const filled = br.entry_filled_mq;
    const toHedge = floorContracts(filled - br.hedged_mq); // never over-hedge a fractional fill
    if (toHedge > 0) {
      const res = await this.placeOrder({
        ticker: br.ticker,
        side: br.hedge_side,
        priceMc: br.hedge_price_mc,
        qtyMq: toHedge,
        mode: 'post',
        accountId: br.account_id,
        source: 'bracket-hedge',
      });
      // Re-fetch under the still-held lock: the bracket may have been cancelled while placeOrder awaited.
      br = this.brackets.get(entryOrderId);
      if (!br) return;
      if (res.ok) {
        const hid = res.order.order_id;
        if (hid) {
          br.hedge_order_ids.push(hid);
          this.bracketHedges.set(hid, entryOrderId);
        }
        br.hedged_mq += toHedge;
        this.log('info', `bracket ${entryOrderId}: hedged +${toHedge / MQ_PER_CONTRACT} @ ${br.hedge_price_mc}mc`);
        await this.saveBrackets();
        this.markDirty(br.ticker);
      } else {
        this.log('error', `bracket ${entryOrderId}: hedge of ${toHedge / MQ_PER_CONTRACT} FAILED: ${res.error}`);
      }
    }

    if (br.entry_filled_mq >= br.orig_mq && br.hedged_mq >= br.entry_filled_mq) {
      this.brackets.delete(entryOrderId);
      this.hedgeLocks.delete(entryOrderId);
      await this.saveBrackets();
    }
  }

  // Parks order IDs for every armed bracket entry in the service worker (which signs cancels at fire time),
  // refreshed on a 5s tick, so a closed tab still cancels entries rather than leaving them unhedged.
  private deadmanEnabled = true;
  private deadmanArmed = false;
  /** Resting orders other engines want cancelled if this tab dies: arb quotes. */
  private deadmanExtras = new Map<string, string>();

  noteDeadmanOrder(orderId: string, ticker: string): void {
    this.deadmanExtras.set(orderId, ticker);
    void this.refreshDeadman();
  }

  forgetDeadmanOrder(orderId: string): void {
    if (this.deadmanExtras.delete(orderId)) void this.refreshDeadman();
  }

  /** Teach the engine an order it did not place, so a cancel by id can route. */
  noteOrder(orderId: string, ticker: string): void {
    this.orderIndex.set(orderId, ticker);
  }
  private deadmanLastError = '';

  setDeadmanEnabled(on: boolean): void {
    this.deadmanEnabled = on;
    if (!on) void this.clearDeadman();
    else void this.refreshDeadman();
  }

  isDeadmanEnabled(): boolean {
    return this.deadmanEnabled;
  }

  private async clearDeadman(): Promise<void> {
    if (!this.deadmanArmed) return; // don't message the worker every tick for nothing
    this.deadmanArmed = false;
    await callBridge({ type: 'DEADMAN_CLEAR' }, 4_000);
  }

  async refreshDeadman(): Promise<void> {
    if (!this.deadmanEnabled) return;
    // Each entry carries its ticker: the worker's cancels need it to route to the order's exchange shard.
    const orders = [
      ...[...this.brackets].map(([orderId, b]) => ({ orderId, ticker: b.ticker })),
      ...[...this.deadmanExtras].map(([orderId, ticker]) => ({ orderId, ticker })),
    ];
    if (!orders.length) {
      await this.clearDeadman();
      return;
    }
    const resp = await callBridge({ type: 'DEADMAN_SET', orders, graceMs: 45_000 }, 6_000);
    if (resp.ok) {
      this.deadmanArmed = true;
      this.deadmanLastError = '';
      return;
    }
    // callBridge never throws; a refused/timed-out arm comes back ok:false. Logged on change only (runs every 5s).
    this.deadmanArmed = false;
    const err = String(resp.error || 'no response');
    if (err !== this.deadmanLastError) {
      this.deadmanLastError = err;
      this.log('error', `deadman NOT armed — ${orders.length} bracket entr${orders.length === 1 ? 'y' : 'ies'} unprotected: ${err}`);
    }
  }

  /** Called from pagehide. Fire-and-forget; the page is going away. */
  fireDeadman(): void {
    if (!this.deadmanEnabled || !this.brackets.size) return;
    void callBridge({ type: 'DEADMAN_FIRE' }, 3_000);
  }

  hasArmedBrackets(): boolean {
    return this.brackets.size > 0;
  }

  /** Rebuild the local ledger from Kalshi's own fill history; fills with no/unknown coid land in mobile. */
  async rebuildLedgerFromKalshi(sinceTs?: number): Promise<{ fetched: number; added: number }> {
    const raw = await this.client.getFills({ minTs: sinceTs });
    const rows: FillRow[] = [];
    for (const f of raw) {
      const mq = countToMq(f.count_fp ?? f.count) ?? 0;
      const yesMc = dollarsToMc(f.yes_price_dollars);
      if (!mq || yesMc === null) continue;
      const coid: string = f.client_order_id ?? '';
      const orderId: string = f.order_id ?? '';
      let side: 'yes' | 'no';
      const oside = String(f.outcome_side ?? '').toLowerCase();
      if (oside === 'yes' || oside === 'no') {
        side = oside;
      } else {
        side = (f.side ?? 'yes') as 'yes' | 'no';
        if (String(f.action ?? 'buy').toLowerCase() === 'sell') side = side === 'yes' ? 'no' : 'yes';
      }
      const ts = Number(f.ts ?? 0) || 0;
      const isTaker = Boolean(f.is_taker);
      const fid = f.trade_id ?? f.fill_id ?? `${orderId}:${ts}:${mq}:${yesMc}:${isTaker ? 1 : 0}`;
      rows.push({
        fill_id: fid,
        ts,
        ticker: f.ticker ?? f.market_ticker ?? '',
        side,
        mq,
        yes_price_mc: yesMc,
        fee_cash: dollarsToCash(f.fee ?? f.fee_dollars) ?? 0,
        is_taker: isTaker,
        order_id: orderId,
        client_order_id: coid,
        account_id: this.attributeAccount(orderId, coid, fid),
      });
    }
    const added = await appendFills(rows);
    await this.accounts.replay();
    await this.seedMarkers();
    for (const t of this.states.keys()) this.markDirty(t);
    this.markPnlDirty();
    return { fetched: rows.length, added };
  }

  async reloadAccounts(): Promise<void> {
    await this.accounts.load();
    await this.seedMarkers();
    this.emit('accounts', { leaves: this.accounts.listLeaves() });
    for (const t of this.states.keys()) this.markDirty(t);
    this.markPnlDirty();
  }

  async createAccount(name: string): Promise<{ ok: true; account: { id: string; name: string } } | { ok: false; error: string }> {
    try {
      const acct = await this.accounts.create(name);
      this.emit('accounts', { leaves: this.accounts.listLeaves() });
      return { ok: true, account: { id: acct.id, name: acct.name } };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }

  buildUiSnapshot(ticker: string): UiSnapshot {
    this.rollMarkers();
    const state = this.states.get(ticker);
    const empty: UiSnapshot = {
      type: 'snapshot', ticker,
      stats: {
        bestYesBid: null, bestYesAsk: null, midYesDeci: null, openOrders: 0, bidOrders: 0,
        askOrders: 0, volume: 0, positionMq: 0, pnlCash: null, tickMc: 1,
        crossed: false, marketStatus: 'open', marketResult: '',
      },
      rows: [], orders: [], fills: {}, brackets: {}, pnlByAccount: {},
    };
    if (!state) return empty;
    this.recomputeTop(state);

    // Per-level my-order info in O(orders), not O(levels x orders).
    const levelMap = new Map<MC, { yes: MQ; no: MQ; count: number; minQp: MQ | null }>();
    for (const [oid, o] of state.myOrders) {
      if (!(ACTIVE_STATUSES as readonly string[]).includes(o.status)) continue;
      const qty = o.remaining_mq;
      const ep = state.estAhead.get(oid);
      let key: MC | null = null;
      let isYes = false;
      if (o.side === 'yes' && o.yes_price_mc !== null) {
        key = o.yes_price_mc;
        isYes = true;
      } else if (o.side === 'no' && o.no_price_mc !== null) {
        key = MC_SETTLE - o.no_price_mc;
      }
      if (key === null) continue;
      let e = levelMap.get(key);
      if (!e) {
        e = { yes: 0, no: 0, count: 0, minQp: null };
        levelMap.set(key, e);
      }
      if (isYes) e.yes += qty;
      else e.no += qty;
      e.count++;
      if (ep !== undefined && (e.minQp === null || ep < e.minQp)) e.minQp = ep;
    }

    const tickMc = state.priceLevelStructure === 'linear_cent' ? 10 : 1;
    const rows: LadderRow[] = [];
    for (let p = MC_SETTLE - tickMc; p > 0; p -= tickMc) {
      const bid = state.yesBids.get(p) ?? 0;
      const ask = state.noBids.get(MC_SETTLE - p) ?? 0;
      const e = levelMap.get(p);
      if (e) {
        const total = e.yes + e.no;
        const side = e.yes > 0 && e.no === 0 ? 'y'
          : e.no > 0 && e.yes === 0 ? 'n'
            : total > 0 ? 'b' : '';
        const queuePos = e.minQp === null ? null : Math.round(e.minQp / MQ_PER_CONTRACT);
        rows.push([total, bid, p, ask, queuePos, e.count, side]);
      } else {
        rows.push([0, bid, p, ask, null, 0, '']);
      }
    }

    let unrealized: Cash = 0;
    if (state.positionMq !== 0 && state.avgCostMc && state.midYesDeci !== null) {
      unrealized = Math.round(state.positionMq * (state.midYesDeci / 10 - state.avgCostMc));
    }
    const totalPnl = state.realizedCash + unrealized - state.feesCash;

    return {
      type: 'snapshot',
      ticker,
      stats: {
        bestYesBid: state.bestYesBid,
        bestYesAsk: state.bestYesAsk,
        midYesDeci: state.midYesDeci,
        openOrders: state.openOrders,
        bidOrders: state.bidOrders,
        askOrders: state.askOrders,
        volume: state.volumeRest + state.volumeWs,
        positionMq: state.positionMq,
        pnlCash: state.positionMq !== 0 ? totalPnl : null,
        tickMc,
        crossed: state.crossed,
        marketStatus: state.marketStatus,
        marketResult: state.marketResult,
      },
      rows,
      orders: [...state.myOrders.values()].map(orderToUi),
      fills: markersToPlain(this.fillMarkers.get(ticker)),
      brackets: this.bracketLevels(ticker),
      pnlByAccount: this.tickerAccountPnls(ticker),
    };
  }

  private bracketLevels(ticker: string): Record<string, { entry: MC[]; hedge: MC[] }> {
    const state = this.states.get(ticker);
    const out: Record<string, { entry: MC[]; hedge: MC[] }> = {};
    for (const [eid, b] of this.brackets) {
      if (b.ticker !== ticker) continue;
      const d = out[b.account_id] ?? (out[b.account_id] = { entry: [], hedge: [] });
      const o = state?.myOrders.get(eid);
      if (o) {
        const row = o.side === 'yes' ? o.yes_price_mc : (o.no_price_mc !== null ? MC_SETTLE - o.no_price_mc : null);
        if (row !== null) d.entry.push(row);
      }
      d.hedge.push(b.hedge_side === 'yes' ? b.hedge_price_mc : MC_SETTLE - b.hedge_price_mc);
    }
    return out;
  }

  private settleResultMap(): Map<string, string> {
    const res = new Map<string, string>();
    for (const s of this.settlements) {
      const tk = s.ticker;
      const r = String(s.market_result ?? '').toLowerCase();
      if (tk && (r === 'yes' || r === 'no')) res.set(tk, r);
    }
    for (const [tk, st] of this.states) {
      if (['settled', 'finalized', 'resolved'].includes(st.marketStatus)
        && (st.marketResult === 'yes' || st.marketResult === 'no')) {
        res.set(tk, st.marketResult);
      }
    }
    return res;
  }

  /** Per-(account, ticker) display row; realizes an open position at the settlement value once resolved. */
  private posRow(ticker: string, ps: PosState, resMap: Map<string, string>): PositionRow | null {
    const pos = ps.posMq;
    const avg = avgPxMc(ps);
    let realized = ps.realizedCash;
    const fees = ps.feesCash;
    let midMc: number | null = null;
    let unreal: Cash = 0;
    let settled = false;
    let deployed: Cash = 0;
    let shownPos = pos;

    const result = resMap.get(ticker);
    if ((result === 'yes' || result === 'no') && pos !== 0) {
      const settlePx = result === 'yes' ? MC_SETTLE : 0;
      realized += Math.round((pos > 0 ? 1 : -1) * Math.abs(pos) * (settlePx - avg));
      settled = true;
      shownPos = 0;
    } else if (pos !== 0) {
      const st = this.states.get(ticker);
      if (st && st.midYesDeci !== null) {
        midMc = st.midYesDeci / 10;
        unreal = Math.round(pos * (midMc - avg));
      }
      deployed = costOf(ps);
    }

    if (shownPos === 0 && realized === 0 && fees === 0) return null;
    return {
      ticker,
      positionMq: shownPos,
      avgCostMc: shownPos !== 0 || settled ? avg : null,
      midMc,
      settled,
      realizedCash: realized,
      unrealizedCash: midMc !== null ? unreal : null,
      feesCash: fees,
      pnlCash: realized + unreal - fees,
      deployedCash: deployed,
    };
  }

  /** Per-account PnL rows; mobile is the residual (Kalshi aggregate minus the sum of tracked accounts). */
  private accountRows(): AccountRow[] {
    const resMap = this.settleResultMap();
    const agg = new Map<string, any>(this.positions.map((p) => [p.ticker, p]));
    const nmPos = new Map<string, MQ>();
    const nmCost = new Map<string, Cash>();
    const nmReal = new Map<string, Cash>();
    const nmFees = new Map<string, Cash>();
    for (const acct of this.accounts.leaves()) {
      if (isMobile(acct)) continue;
      for (const [tk, raw] of acct.positions) {
        const ps = this.settledState(tk, raw, resMap);
        nmPos.set(tk, (nmPos.get(tk) ?? 0) + ps.posMq);
        nmCost.set(tk, (nmCost.get(tk) ?? 0) + costOf(ps));
        nmReal.set(tk, (nmReal.get(tk) ?? 0) + ps.realizedCash);
        nmFees.set(tk, (nmFees.get(tk) ?? 0) + ps.feesCash);
      }
    }

    const out: AccountRow[] = [];
    for (const acct of this.accounts.leaves()) {
      const source = new Map<string, PosState>();
      if (isMobile(acct)) {
        for (const tk of new Set([...agg.keys(), ...nmPos.keys()])) {
          const residual = this.residualPos(tk, agg.get(tk), nmPos, nmCost, nmReal, nmFees);
          if (residual) source.set(tk, residual);
        }
      } else {
        for (const [tk, ps] of acct.positions) source.set(tk, { ...ps });
      }
      const positions: PositionRow[] = [];
      let real = 0, unreal = 0, fees = 0, dep = 0;
      for (const [tk, ps] of source) {
        const row = this.posRow(tk, ps, resMap);
        if (!row) continue;
        positions.push(row);
        real += row.realizedCash;
        unreal += row.unrealizedCash ?? 0;
        fees += row.feesCash;
        dep += row.deployedCash;
      }
      positions.sort((a, b) => a.ticker.localeCompare(b.ticker));
      out.push({
        id: acct.id,
        name: acct.name,
        parts: acct.parts,
        realizedCash: real,
        unrealizedCash: unreal,
        feesCash: fees,
        pnlCash: real + unreal - fees,
        deployedCash: dep,
        positionMq: positions.reduce((s, p) => s + p.positionMq, 0),
        positions,
      });
    }
    return out;
  }

  /** A position as it stands AFTER settlement (payout folded into realized), so it matches Kalshi's own numbers. */
  private settledState(ticker: string, ps: PosState, resMap: Map<string, string>): PosState {
    const result = resMap.get(ticker);
    if ((result !== 'yes' && result !== 'no') || ps.posMq === 0) return ps;
    const settlePx = result === 'yes' ? MC_SETTLE : 0;
    const avg = avgPxMc(ps);
    return {
      posMq: 0,
      costCash: 0,
      realizedCash: ps.realizedCash + Math.round(ps.posMq * (settlePx - avg)),
      feesCash: ps.feesCash,
    };
  }

  private residualPos(
    tk: string, p: any,
    nmPos: Map<string, MQ>, nmCost: Map<string, Cash>,
    nmReal: Map<string, Cash>, nmFees: Map<string, Cash>,
  ): PosState | null {
    const kpos = p ? countToMq(p.position_fp) ?? 0 : 0;
    const kexp = p ? dollarsToCash(p.market_exposure_dollars) ?? 0 : 0;
    const kreal = p ? dollarsToCash(p.realized_pnl_dollars) ?? 0 : 0;
    const kfees = p ? dollarsToCash(p.fees_paid_dollars) ?? 0 : 0;
    const rpos = kpos - (nmPos.get(tk) ?? 0);
    const rcost = kexp - (nmCost.get(tk) ?? 0);
    const rreal = kreal - (nmReal.get(tk) ?? 0);
    const rfees = kfees - (nmFees.get(tk) ?? 0);
    if (rpos === 0 && rreal === 0 && rfees === 0) return null;
    let avg = 0;
    if (rpos !== 0 && rcost > 0) {
      const px = rcost / Math.abs(rpos);            // avg price of the side held
      avg = Math.max(0, Math.min(MC_SETTLE, rpos < 0 ? MC_SETTLE - px : px)); // -> YES-equivalent
    }
    return { posMq: rpos, costCash: Math.round(rpos * avg), realizedCash: rreal, feesCash: rfees };
  }

  private tickerAccountPnls(ticker: string): Record<string, PositionRow> {
    const resMap = this.settleResultMap();
    const agg = this.positions.find((p) => p.ticker === ticker);
    const nmPos = new Map<string, MQ>();
    const nmCost = new Map<string, Cash>();
    const nmReal = new Map<string, Cash>();
    const nmFees = new Map<string, Cash>();
    for (const a of this.accounts.leaves()) {
      if (isMobile(a)) continue;
      const raw = a.positions.get(ticker);
      if (!raw) continue;
      const ps = this.settledState(ticker, raw, resMap);
      nmPos.set(ticker, (nmPos.get(ticker) ?? 0) + ps.posMq);
      nmCost.set(ticker, (nmCost.get(ticker) ?? 0) + costOf(ps));
      nmReal.set(ticker, (nmReal.get(ticker) ?? 0) + ps.realizedCash);
      nmFees.set(ticker, (nmFees.get(ticker) ?? 0) + ps.feesCash);
    }
    const out: Record<string, PositionRow> = {};
    for (const acct of this.accounts.leaves()) {
      let ps: PosState | null | undefined;
      if (isMobile(acct)) ps = this.residualPos(ticker, agg, nmPos, nmCost, nmReal, nmFees);
      else ps = acct.positions.get(ticker);
      if (!ps) continue;
      const row = this.posRow(ticker, ps, resMap);
      if (row) out[acct.id] = row;
    }
    return out;
  }

  buildPnlSnapshot(): PnlSnapshot {
    const accounts = this.accountRows();
    const totalPnl = accounts.reduce((s, a) => s + a.pnlCash, 0);

    const tree = pnlTree(accounts);

    // Resting orders grouped by (ticker, account) so global Orders Out can show which account owns each.
    const grp = new Map<string, { ticker: string; account: string; bids: number; asks: number; bracket: number }>();
    const bump = (tk: string, acc: string) => {
      const key = `${tk}\u0000${acc}`;
      let d = grp.get(key);
      if (!d) {
        d = { ticker: tk, account: acc, bids: 0, asks: 0, bracket: 0 };
        grp.set(key, d);
      }
      return d;
    };
    for (const [tk, st] of this.states) {
      for (const o of st.myOrders.values()) {
        if (!(ACTIVE_STATUSES as readonly string[]).includes(o.status)) continue;
        const d = bump(tk, o.account_id ?? '');
        if (o.side === 'yes') d.bids++;
        else d.asks++;
        if (o.source === 'bracket' || o.source === 'bracket-hedge') d.bracket++;
      }
    }
    for (const od of this.allResting.values()) {
      if (!od.ticker || this.states.has(od.ticker)) continue; // subscribed tickers counted above
      const acc = this.orderAccounts.get(od.order_id) ?? accountFromCoid(od.client_order_id) ?? MOBILE_ID;
      const d = bump(od.ticker, acc);
      if (od.side === 'yes') d.bids++;
      else d.asks++;
      if (this.brackets.has(od.order_id) || this.bracketHedges.has(od.order_id)) d.bracket++;
    }
    const ordersOut: OrdersOutRow[] = [...grp.values()].map((d) => {
      const a = this.accounts.get(d.account);
      return {
        ticker: d.ticker, account_id: d.account, account: a?.name ?? (d.account || '—'),
        bids: d.bids, asks: d.asks, bracket: d.bracket,
      };
    }).sort((x, y) => x.ticker.localeCompare(y.ticker) || x.account.localeCompare(y.account));

    const settledRecent: SettledRow[] = [];
    const settledTickers = new Set(this.settlements.map((s) => s.ticker));
    for (const pos of this.positions) {
      if ((countToMq(pos.position_fp) ?? 0) !== 0) continue;
      const realized = dollarsToCash(pos.realized_pnl_dollars) ?? 0;
      const fees = dollarsToCash(pos.fees_paid_dollars) ?? 0;
      if (realized === 0 && fees === 0) continue;
      if (settledTickers.has(pos.ticker)) continue;
      settledRecent.push({ ticker: pos.ticker ?? '', result: 'closed', pnlCash: realized - fees, settled_time: '' });
    }

    const cutoff = Date.now() - 24 * 3600 * 1000;
    const settledAll: SettledRow[] = [];
    for (const s of this.settlements) {
      const yesMq = countToMq(s.yes_count_fp) ?? 0;
      const noMq = countToMq(s.no_count_fp) ?? 0;
      if (yesMq === 0 && noMq === 0) continue;
      const yesCost = dollarsToCash(s.yes_total_cost_dollars) ?? 0;
      const noCost = dollarsToCash(s.no_total_cost_dollars) ?? 0;
      const fee = dollarsToCash(s.fee_cost) ?? 0;
      // `value` is the YES settlement value in cents (100 or 0).
      const valYesMc = Math.round(Number(s.value ?? 0) * 10);
      const payout = cashOf(yesMq, valYesMc) + cashOf(noMq, MC_SETTLE - valYesMc);
      const pnl = payout - yesCost - noCost - fee;
      const tsStr: string = s.settled_time ?? '';
      const entry: SettledRow = {
        ticker: s.ticker ?? '', result: s.market_result ?? '', pnlCash: pnl, settled_time: tsStr.slice(0, 10),
      };
      const parsed = Date.parse(tsStr);
      if (Number.isFinite(parsed) && parsed >= cutoff) settledRecent.push(entry);
      else settledAll.push(entry);
    }

    return {
      type: 'pnl_update',
      balanceCash: this.balanceCash,
      totalPnlCash: totalPnl,
      accounts,
      tree,
      orders_out: ordersOut,
      settled_recent: settledRecent,
      settled: settledAll,
    };
  }

  /** Recent fills for a ticker, newest first, optionally scoped to one account. */
  async recentFills(ticker: string, accountId?: string, limit = 100): Promise<FillRow[]> {
    const rows = (await allFills()).filter((f) =>
      f.ticker === ticker && (!accountId || f.account_id === accountId));
    rows.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    return rows.slice(0, limit);
  }

  /** Plain-English event search, served from the locally built index. */
  async searchEvents(q: string, limit?: number): Promise<{ hits: ExploreEvent[]; total: number }> {
    const { hits, total } = searchEvents(q, limit);
    // The series line is shown because it's often the only thing that explains why a hit matched.
    return {
      hits: hits.map((e) => {
        const s = seriesOf(e);
        return { id: e.ticker, title: e.title, tag: s?.tags?.[0] || e.category, series: s?.title };
      }),
      total,
    };
  }

  exploreState(): ExploreState {
    return this.eventIndexState();
  }

  /** The series an event sits under — "WTA Tennis Match" for a "X vs Y" event. */
  seriesOf(e: EventRow): SeriesRow | undefined {
    return seriesOf(e);
  }

  /** Every fill grouped by the account it is booked to, newest first; reads the ledger, keeps no second index. */
  async fillsByAccount(): Promise<Map<string, FillRow[]>> {
    const out = new Map<string, FillRow[]>();
    for (const a of this.accounts.accounts.keys()) out.set(a, []);
    for (const f of await allFills()) {
      const key = out.has(f.account_id) ? f.account_id : MOBILE_ID;
      (out.get(key) ?? out.set(key, []).get(key)!).push(f);
    }
    for (const list of out.values()) list.sort((a, b) => b.ts - a.ts);
    return out;
  }

  /** Delete an account and rebook every one of its fills to the mobile residual; no fill is destroyed. */
  async deleteAccount(accountId: string): Promise<{ moved: number }> {
    if (accountId === MOBILE_ID) throw new Error('the mobile residual cannot be deleted');
    const acct = this.accounts.get(accountId);
    if (!acct) throw new Error(`no such account: ${accountId}`);
    const name = acct.name;

    let moved = 0;
    for (const f of await allFills()) {
      if (f.account_id !== accountId) continue;
      await updateFillAccount(f.fill_id, MOBILE_ID);
      moved++;
    }
    for (const [fillId, aid] of [...this.fillOverrides]) {
      if (aid === accountId) this.fillOverrides.delete(fillId);
    }
    await setFillOverrides(Object.fromEntries(this.fillOverrides));
    await this.accounts.remove(accountId);
    await this.afterLedgerEdit();
    this.log('info', `deleted account ${name} — ${moved} fill(s) rebooked to mobile`);
    return { moved };
  }

  /** True when this fill's account was set by hand rather than derived. */
  isFillMoved(fillId: string): boolean {
    return this.fillOverrides.has(fillId);
  }

  /** Book a fill to a different account; positions/PnL are replayed from the whole ledger, not patched. */
  async moveFill(fillId: string, accountId: string): Promise<void> {
    if (!this.accounts.get(accountId)) throw new Error(`unknown account ${accountId}`);
    if (!(await updateFillAccount(fillId, accountId))) throw new Error(`unknown fill ${fillId}`);
    this.fillOverrides.set(fillId, accountId);
    await setFillOverrides(Object.fromEntries(this.fillOverrides));
    await this.afterLedgerEdit();
    this.log('info', `fill ${fillId.slice(0, 12)} moved to ${this.accounts.get(accountId)?.name}`);
  }

  /** Drop a manual reassignment and fall back to the derived account. */
  async resetFillAccount(fillId: string): Promise<void> {
    if (!this.fillOverrides.delete(fillId)) return;
    await setFillOverrides(Object.fromEntries(this.fillOverrides));
    const rows = await allFills();
    const row = rows.find((f) => f.fill_id === fillId);
    if (row) {
      await updateFillAccount(fillId, this.attributeAccount(row.order_id, row.client_order_id));
    }
    await this.afterLedgerEdit();
  }

  private async afterLedgerEdit(): Promise<void> {
    await this.accounts.replay();
    await this.seedMarkers();
    this.emit('accounts', { leaves: this.accounts.listLeaves() });
    for (const t of this.states.keys()) this.markDirty(t);
    this.markPnlDirty();
  }

  /** The manual reassignments, as JSON; the only part of attribution not reconstructible from Kalshi. */
  exportFillOverrides(): string {
    return JSON.stringify({
      kind: 'kladder-fill-overrides', version: 1,
      exported: new Date().toISOString(),
      overrides: Object.fromEntries(this.fillOverrides),
    }, null, 2);
  }

  /** Merge in an exported map. Returns how many fills actually moved. */
  async importFillOverrides(json: string): Promise<number> {
    const data = JSON.parse(json);
    const map = (data?.overrides ?? data) as Record<string, string>;
    if (!map || typeof map !== 'object') throw new Error('not an overrides file');
    let applied = 0;
    for (const [fillId, accountId] of Object.entries(map)) {
      if (typeof accountId !== 'string' || !this.accounts.get(accountId)) continue;
      // Silently skipped when the fill is not in this ledger — importing before
      // a rebuild is a normal order of operations on a fresh machine.
      if (!(await updateFillAccount(fillId, accountId))) continue;
      this.fillOverrides.set(fillId, accountId);
      applied++;
    }
    await setFillOverrides(Object.fromEntries(this.fillOverrides));
    await this.afterLedgerEdit();
    return applied;
  }

  eventIndexState(): { building: boolean; count: number } {
    return { building: isEventIndexBuilding(), count: eventRows().length };
  }

  /** Rebuild the event search index now, ignoring the TTL. Reachable from the console as `kg.engine.rebuildEvents()`. */
  rebuildEvents(): Promise<void> {
    return rebuildEventIndex(this.client);
  }

  /** Recent bid/ask history for a ticker, as {t, bid, ask} in MC; candles are dense so quote lines have no holes. */
  async bidAskHistory(
    ticker: string, minutes = 120, interval: 1 | 60 = 1,
  ): Promise<HistPoint[]> {
    const series = ticker.split('-')[0];
    const end = Math.floor(Date.now() / 1000);
    const rows = await this.client.getCandles(series, ticker, end - minutes * 60, end, interval);
    return rows.map((c: any) => ({
      t: Number(c.end_period_ts) * 1000,
      bid: dollarsToMc(c.yes_bid?.close_dollars),
      ask: dollarsToMc(c.yes_ask?.close_dollars),
      // Hi/lo shown separately: close alone hides most within-minute bid movement on a busy market.
      bidHi: dollarsToMc(c.yes_bid?.high_dollars),
      bidLo: dollarsToMc(c.yes_bid?.low_dollars),
      askHi: dollarsToMc(c.yes_ask?.high_dollars),
      askLo: dollarsToMc(c.yes_ask?.low_dollars),
    })).filter((r) => r.bid !== null || r.ask !== null);
  }

  /** Traded-price OHLC bars from candles; unlike the quote lines, gaps here are real and not filled in. */
  async priceBars(ticker: string, minutes = 120, interval: 1 | 60 = 1): Promise<Bar[]> {
    const series = ticker.split('-')[0];
    const end = Math.floor(Date.now() / 1000);
    const rows = await this.client.getCandles(series, ticker, end - minutes * 60, end, interval);
    const span = interval * 60_000;
    const out: Bar[] = [];
    for (const c of rows) {
      const o = dollarsToMc(c.price?.open_dollars);
      const h = dollarsToMc(c.price?.high_dollars);
      const l = dollarsToMc(c.price?.low_dollars);
      const cl = dollarsToMc(c.price?.close_dollars);
      const vol = countToMq(c.volume_fp) ?? 0;
      // A period with no trades still returns a candle, carrying the previous price forward at zero volume.
      if (o === null || h === null || l === null || cl === null || vol <= 0) continue;
      const t1 = Number(c.end_period_ts) * 1000;
      out.push({ t0: t1 - span, t1, o, h, l, c: cl, vol, n: 0 });
    }
    return out;
  }

  /** The most recent `want` public prints, oldest first; counted rather than time-boxed since callers count trades. */
  async tradeHistory(
    ticker: string, want: number, maxPages = 20,
  ): Promise<{ trades: Trade[]; exhausted: boolean; hitCap: boolean }> {
    const out: Trade[] = [];
    let cursor = '';
    let pages = 0;
    for (; pages < maxPages && out.length < want; pages++) {
      const page = await this.client.getTrades({ ticker, limit: 1000, cursor });
      for (const r of page.trades) {
        const priceMc = dollarsToMc(r.yes_price_dollars);
        const mq = countToMq(r.count_fp ?? r.count) ?? 0;
        const t = Date.parse(r.created_time);
        if (priceMc === null || !mq || !Number.isFinite(t)) continue;
        out.push({ t, priceMc, mq, taker: r.taker_side === 'no' ? 'no' : 'yes' });
      }
      cursor = page.cursor;
      if (!cursor || !page.trades.length) break;
    }
    out.sort((a, b) => a.t - b.t);
    // Two endings: ran out of history vs. stopped paging; callers need to tell the two apart.
    const hitCap = pages >= maxPages && !!cursor;
    if (hitCap) {
      this.log('warn', `trade history for ${ticker} hit the ${maxPages}-page cap — older prints omitted`);
    }
    // Keep the newest `want`; the last page usually overshoots.
    const trades = out.slice(Math.max(0, out.length - want));
    return { trades, exhausted: !cursor, hitCap };
  }

  /** Traded volume by price level (yes-MC); `complete` is false when the market printed more than we paged. */
  async volumeProfile(
    ticker: string, maxTrades = 20_000,
  ): Promise<{ byPrice: Map<MC, MQ>; trades: number; complete: boolean }> {
    const { trades, exhausted } = await this.tradeHistory(ticker, maxTrades);
    const byPrice = new Map<MC, MQ>();
    for (const tr of trades) byPrice.set(tr.priceMc, (byPrice.get(tr.priceMc) ?? 0) + tr.mq);
    return { byPrice, trades: trades.length, complete: exhausted };
  }

  /** The contracts under one event, with live quotes. One request. */
  async eventMarkets(eventTicker: string): Promise<ExploreMarket[]> {
    const rows = await this.client.getMarkets({ event_ticker: eventTicker, limit: 100 });
    return rows.map((m: any) => ({
      ticker: String(m.ticker ?? ''),
      title: String(m.yes_sub_title || m.title || m.ticker || ''),
      bidMc: dollarsToMc(m.yes_bid_dollars),
      askMc: dollarsToMc(m.yes_ask_dollars),
      volume: Number.isFinite(Number(m.volume_fp)) ? Number(m.volume_fp) : null,
      status: String(m.status ?? 'open'),
    }));
  }


  /** Market search: try the event ticker first (exact-ish), fall back to the whole series, then filter client-side. */
  async searchMarkets(q: string): Promise<{ ticker: string; title: string; status: string }[]> {
    const qRaw = q.trim().toUpperCase();
    const qApi = qRaw.replace(/-+$/, '');
    if (qApi.length < 2) return [];

    const fetchSafe = async (params: Record<string, string | number>) => {
      try {
        return await this.client.getMarkets(params);
      } catch {
        return [];
      }
    };

    const parts = qApi.split('-');
    const series = parts[0];
    const eventTicker = parts.length >= 3 ? parts.slice(0, -1).join('-') : parts.length === 2 ? qApi : null;

    const seen = new Set<string>();
    let results: any[] = [];
    if (eventTicker) {
      for (const m of await fetchSafe({ event_ticker: eventTicker, limit: 100 })) {
        if (!seen.has(m.ticker)) {
          seen.add(m.ticker);
          results.push(m);
        }
      }
    }
    if (!results.length) {
      for (const m of await fetchSafe({ series_ticker: series, limit: 1000 })) {
        if (!seen.has(m.ticker)) {
          seen.add(m.ticker);
          results.push(m);
        }
      }
    }
    if (qRaw && qRaw !== series) results = results.filter((m) => m.ticker.startsWith(qRaw));
    if (parts.length < 3) {
      results = results.filter((m) => !['settled', 'resolved', 'finalized'].includes(m.status ?? 'open'));
    }
    results.sort((a, b) => a.ticker.localeCompare(b.ticker));
    return results.slice(0, 40).map((m) => ({ ticker: m.ticker, title: m.title ?? '', status: m.status ?? '' }));
  }
}

function levelsToMap(levels: any): Map<MC, MQ> {
  const out = new Map<MC, MQ>();
  for (const [p, q] of levels ?? []) {
    const mc = dollarsToMc(p);
    const mq = countToMq(q);
    if (mc !== null && mq !== null) out.set(mc, mq);
  }
  return out;
}

function maxKey(m: Map<number, number>): number | null {
  let best: number | null = null;
  for (const k of m.keys()) if (best === null || k > best) best = k;
  return best;
}

function orderToUi(o: Order): UiOrder {
  return {
    order_id: o.order_id,
    client_order_id: o.client_order_id,
    ticker: o.ticker,
    status: o.status,
    side: o.side,
    price_mc: o.side === 'yes' ? o.yes_price_mc : o.no_price_mc,
    remaining_mq: o.remaining_mq,
    queue_position: o.queue_position,
    created_time: o.created_time,
    source: o.source,
    account_id: o.account_id,
  };
}

function markersToPlain(m: Map<string, Map<MC, [number, number]>> | undefined): Record<string, Record<number, [number, number]>> {
  const out: Record<string, Record<number, [number, number]>> = {};
  if (!m) return out;
  for (const [acct, levels] of m) {
    const o: Record<number, [number, number]> = {};
    for (const [lvl, cell] of levels) o[lvl] = cell;
    out[acct] = o;
  }
  return out;
}

/** Re-exported so the UI can render a blank position without importing accounts. */
export { blankPos };
export type { Account, PosState };
